import {
  DataLayerLogType,
  FluidDataLayerComponentConfiguration,
  FluidDataLayerContext,
  FluidDataLayerEvent,
  FluidHTMLElement,
} from '@lmig/fluid-core';

import { DATA_LAYER } from '../../constants/constants';
import { Utils } from '../../utils/utils';
import { setDataAtPath } from '../fluid-table/internal/fluid-table-data-management.utils';

export const sendEvent = (host, parentLayerId, event, detail?) => {
  if (eventIsTracked(host, event)) {
    FluidDataLayerUtils.propagateEvent(
      new CustomEvent(event, { detail }),
      host,
      parentLayerId
    );
  }
};

/**
 * Returns true if the event should be tracked
 * @param host
 * @param event
 */
const eventIsTracked = (
  host: Partial<{ analyticsConfig: FluidDataLayerComponentConfiguration }>,
  event: string
) => {
  return !!host?.analyticsConfig?.trackEvents?.[event];
};

export class FluidDataLayerUtils {
  static logAction = (
    action: string,
    type: DataLayerLogType,
    includedTypes: DataLayerLogType[]
  ) => {
    if (includedTypes.indexOf(type) !== -1) {
      console.info(`🧪 %c[FLUID DATA LAYER%c] ${action}`, Utils.boldStyle, '');
    }
  };

  static hostIsInIFrame = (): boolean =>
    window.location !== window.parent.location;

  static getWindowInstance = () => {
    if (FluidDataLayerUtils.hostIsInIFrame()) {
      return window.parent.window;
    } else {
      return window;
    }
  };

  static getDataLayer = (host?): HTMLFluidDataLayerElement => {
    let rootDataLayer = null;
    if (FluidDataLayerUtils.hostIsInIFrame()) {
      rootDataLayer = window.parent?.fluidDataLayer?.instance;
    }
    return (
      rootDataLayer ||
      DATA_LAYER ||
      FluidDataLayerUtils.getDataLayerFromShadow(host as HTMLElement)
    );
  };

  static getDataLayerFromShadow = (host: HTMLElement) => {
    const rootElement = host?.getRootNode();
    if (Utils.isShadowNode(rootElement)) {
      let parentElement = host;
      while (parentElement.parentElement) {
        parentElement = parentElement.parentElement;
      }
      return (
        parentElement.querySelector('fluid-data-layer') ||
        parentElement.shadowRoot?.querySelector('fluid-data-layer')
      );
    }
  };

  static getClosestDataLayer = (host: any) => {
    const matcher =
      Element.prototype.matches ||
      Element.prototype['msMatchesSelector'] ||
      Element.prototype.webkitMatchesSelector;
    do {
      if (matcher.call(host, 'fluid-data-layer')) return host;
      host = host?.parentElement || host?.parentNode;
    } while (host?.nodeType === 1);
    return null;
  };

  static propagateEvent = (
    event: CustomEvent | Event,
    host: FluidHTMLElement,
    appId?: string
  ) => {
    const analyticsConfig: FluidDataLayerComponentConfiguration =
      host.analyticsConfig;
    const fluidDataLayer: HTMLFluidDataLayerElement =
      FluidDataLayerUtils.getDataLayer();
    const eventToPropagate = FluidDataLayerUtils.createEvent(
      event,
      analyticsConfig,
      appId
    );
    if (fluidDataLayer && eventToPropagate) {
      fluidDataLayer
        .postEvent(eventToPropagate)
        .then(() =>
          FluidDataLayerUtils.logAction(
            `Propagated ${event.type} event from ${analyticsConfig?.identifier}`,
            DataLayerLogType.EVENT_PROPAGATED,
            fluidDataLayer.includeLogs
          )
        );
    }
  };

  static supplementPayload = (analyticsConfig, payload, eventName?: string) => {
    const path = eventName ? `trackEvents.${eventName}.payload` : 'payload';
    return setDataAtPath(
      analyticsConfig,
      null,
      {
        ...payload,
      },
      path
    );
  };

  static createEvent = (
    event: CustomEvent | Event,
    analyticsConfig: FluidDataLayerComponentConfiguration,
    appId: string
  ) => {
    if (!analyticsConfig) return;
    if (!analyticsConfig.trackEvents[event.type]) return;
    const eventPayload = (event as CustomEvent).detail
      ? (event as CustomEvent).detail
      : {};
    return {
      identifier: analyticsConfig.identifier,
      eventType: analyticsConfig.trackEvents[event.type].alias,
      originatingAppId: appId,
      payload: {
        ...eventPayload,
        ...analyticsConfig.trackEvents[event.type].payload,
        ...FluidDataLayerUtils.getContext(analyticsConfig.context),
        ...FluidDataLayerUtils.getContext(
          analyticsConfig.trackEvents[event.type].context
        ),
      },
      timestamp: new Date(),
    } as FluidDataLayerEvent;
  };

  /**
   * Checks if the context passed t
   * @param context
   */
  static getContext = (context: FluidDataLayerContext) => {
    let finalContext = context;
    if (typeof context === 'function') {
      finalContext = context(window.fluidDataLayer.state);
    }
    return finalContext;
  };
}
